import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { getUnixTime } from 'date-fns';
import { humanize } from 'ember-cli-string-helpers/helpers/humanize';
import { dropTask } from 'ember-concurrency';
import type StoreService from 'ember-data/store';
import type DeskModel from 'garaje/models/desk';
import type MapFeatureModel from 'garaje/models/map-feature';
import type ReservationModel from 'garaje/models/reservation';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import type MetricsService from 'garaje/services/metrics';
import { SECONDS_IN_A_DAY } from 'garaje/utils/enums';
import kebabCase from 'lodash/kebabCase';

/**
 * Next desk assignment data from GraphQL search results
 * Defined locally until schema.graphql is updated with this type
 */
interface NextAssignment {
  deskId: string;
  deskName: string;
  startTime: string;
}

/**
 * Employee data from GraphQL search results
 */
interface EmployeeGraphQLData {
  id: number;
  name: string;
  email: string;
  profilePictureUrl?: string;
  nextAssignment?: NextAssignment;
}

/**
 * Transformed employee data for display and selection
 */
interface EmployeeDisplayItem {
  id: string;
  name: string;
  email: string;
  profilePictureUrl?: string;
  nextAssignment?: NextAssignment;
}

interface MapFeatureSearchResult {
  type: string;
  __typename: 'MapFeatureSearchResultItem';
  feature: {
    id: string;
    name: string;
    floor: {
      id: string;
      name: string;
      __typename: 'MapFloor';
    };
    __typename: 'MapPointOfInterestFeature';
  };
}

interface EmployeeSearchResult {
  type: string;
  __typename: 'EmployeeMapSearchResultItem';
  employee: EmployeeGraphQLData;
}

type MapSearchResultItem = MapFeatureSearchResult | EmployeeSearchResult;

interface SearchResultsListArgs {
  searchResults: MapSearchResultItem[];
  closeDropdown?: () => void;
  mapFeatures: MapFeatureModel[];
  setSearchInput?: (value: string) => void;
  setMapSearchNoResultsText?: (value: string) => void;
  mapType: 'live' | 'edit' | 'draft';
  onEmployeeSelect?: (nextAssignment: NextAssignment | undefined) => void;
  // Location-wide reservations (all floors) loaded by the live-map controller. Used to
  // locate a reservation-only employee whose desk is on a different floor, since the
  // live-map resourceStates are floor-scoped (AMP-2647) and miss cross-floor reservations.
  reservations?: ReservationModel[];
  // The live map's currently selected time window (unix seconds). When set, the
  // cross-floor reservation lookup matches reservations overlapping this window so it
  // lands on the reservation the user is actually viewing.
  selectedStartTime?: number;
  selectedEndTime?: number;
}

export default class SearchResultsList extends Component<SearchResultsListArgs> {
  // Resource types that should be categorized as "Resources"
  private readonly RESOURCE_TYPES = ['PARKING', 'DESK', 'ROOM'];

  @service declare flashMessages: FlashMessagesService;
  @service declare liveMap: LiveMapService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  get resources(): Array<{ id: string; name: string; description: string; type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is MapFeatureSearchResult =>
          item.__typename === 'MapFeatureSearchResultItem' &&
          item.feature?.name !== undefined &&
          this.RESOURCE_TYPES.includes(item.type),
      )
      .map((item) => ({
        id: item.feature.id,
        name: item.feature.name,
        description: `${humanize([item.type])} • ${item.feature.floor.name}`,
        iconClassName: item.type.toLowerCase(),
        floorId: item.feature.floor.id,
        type: 'resource',
      }));
  }

  get pointsOfInterest(): Array<{ id: string; name: string; description: string; type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is MapFeatureSearchResult =>
          item.__typename === 'MapFeatureSearchResultItem' &&
          item.feature?.name !== undefined &&
          !this.RESOURCE_TYPES.includes(item.type),
      )
      .map((item) => ({
        id: item.feature.id,
        name: item.feature.name,
        description: `${humanize([item.type])} • ${item.feature.floor.name}`,
        iconClassName: kebabCase(item.type.toLowerCase()),
        floorId: item.feature.floor.id,
        type: 'point-of-interest',
      }));
  }

  get employees(): Array<EmployeeDisplayItem & { type: string }> {
    return this.args.searchResults
      .filter(
        (item): item is EmployeeSearchResult =>
          item.__typename === 'EmployeeMapSearchResultItem' && item.employee?.name !== undefined,
      )
      .map((item) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        const nextAssignment = (item.employee as any).nextAssignment as NextAssignment | undefined;
        return {
          id: item.employee.id.toString(),
          name: item.employee.name,
          email: item.employee.email,
          profilePictureUrl: item.employee.profilePictureUrl?.includes('gravatar')
            ? undefined
            : item.employee.profilePictureUrl,
          // nextAssignment is not yet in generated GraphQL schema, will be added when backend deploys
          nextAssignment,
          type: 'employee' as const,
        };
      });
  }

  get hasResults(): boolean {
    return this.resources.length > 0 || this.pointsOfInterest.length > 0 || this.employees.length > 0;
  }

  handleNoSearchResults(employeeName: string): void {
    if (this.args.setMapSearchNoResultsText) {
      this.args.setMapSearchNoResultsText(`${employeeName} does not have an assignment in this location`);
    }
  }

  handleSelectEmployeeError(employeeName: string): void {
    this.handleNoSearchResults(employeeName);
    if (this.args.setSearchInput) {
      this.args.setSearchInput(employeeName);
    }
    if (this.args.closeDropdown) {
      this.args.closeDropdown();
    }
  }

  async handleTransition(
    selectedMapFeatureId: string | undefined,
    floorId: string,
    selectedEmployeeId?: string,
  ): Promise<void> {
    switch (this.args.mapType) {
      case 'edit':
        await this.router.transitionTo('spaces.maps.edit.show', {
          queryParams: {
            floor_id: floorId,
            ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
          },
        });
        break;
      case 'live':
        await this.router.transitionTo('spaces.maps.live.show', floorId, {
          queryParams: {
            ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
            selectedEmployeeId: selectedEmployeeId ?? null,
          },
        });
        break;
      case 'draft': {
        // Get the area map ID from the parent route parameters using router.recognize
        const areaMapId = this.router.currentRoute.parent?.params?.['area_map_id'];
        if (areaMapId) {
          await this.router.transitionTo('spaces.maps.drafts.area-map.show', areaMapId, {
            queryParams: {
              floor_id: floorId,
              ...(selectedMapFeatureId ? { selectedFeatureId: selectedMapFeatureId } : {}),
            },
          });
        }
        break;
      }
    }
  }

  @action
  async handleSelectMapFeature(result: { id: string; floorId: string; name: string }): Promise<void> {
    await this.handleTransition(result.id, result.floorId);

    if (this.args.closeDropdown) {
      this.args.closeDropdown();
    }
    if (this.args.setSearchInput) {
      this.args.setSearchInput(result.name);
    }
  }

  // A drop task so a rapid second selection is ignored while the first is still resolving
  // (the on-demand desk fetch widens the window in which a concurrent select could race).
  handleSelectEmployeeTask = dropTask(async (selectedEmployee: EmployeeDisplayItem): Promise<void> => {
    const { mapFeatures, closeDropdown, setSearchInput, onEmployeeSelect } = this.args;

    // Use nextAssignment if available from GraphQL
    if (selectedEmployee.nextAssignment) {
      const { deskId } = selectedEmployee.nextAssignment;

      // Only proceed with GraphQL path if deskId is valid
      if (deskId) {
        // Get floorId from the desk record — works even when employee is on a different floor
        const desk = this.store.peekRecord('desk', deskId);
        const floorId = desk?.belongsTo('floor').id();

        if (floorId) {
          // Find the map feature — may be undefined if the desk is on a different floor
          // than the one currently loaded in mapFeatures
          const selectedMapFeature = mapFeatures.find((mapFeature) => mapFeature.externalId === deskId);

          // Notify controller about the selected nextAssignment for popup display
          if (onEmployeeSelect) {
            onEmployeeSelect(selectedEmployee.nextAssignment);
          }

          // Transition to the correct floor; selectedMapFeature may be undefined for
          // cross-floor searches (desk highlight will not appear but floor redirect will)
          await this.handleTransition(selectedMapFeature?.id, floorId, selectedEmployee.id);

          if (closeDropdown) {
            closeDropdown();
          }
          if (setSearchInput) {
            setSearchInput(selectedEmployee.name);
          }
          return;
        }
      }
    }

    // Legacy fallback: search in local store
    let allDesks = this.store.peekAll('desk').slice();

    // In draft map, only show desks that are part of the draft
    if (this.args.mapType === 'draft') {
      allDesks = allDesks.filter((d: DeskModel) => d.parentDeskId != null);
    }

    let selectedDesk: DeskModel | undefined;
    try {
      selectedDesk = await this.findEmployeeDesk(selectedEmployee, allDesks);
    } catch (error) {
      // A desk lookup failure (e.g. a failed on-demand fetch) is not the same as the
      // employee having no desk — surface a generic failure instead of the misleading
      // "no assignment" message, and log for observability.
      this.metrics.logMonitorError({
        event: 'MAPS_SEARCH_RESERVATION_DESK_LOOKUP_FAILED',
        error,
        debugExtras: { employeeId: selectedEmployee.id },
      });
      this.flashMessages.showAndHideFlash('error', 'Unable to locate the desk for this employee. Please try again.');
      if (setSearchInput) {
        setSearchInput(selectedEmployee.name);
      }
      if (closeDropdown) {
        closeDropdown();
      }
      return;
    }

    if (!selectedDesk) {
      this.handleSelectEmployeeError(selectedEmployee.name);
      return;
    }

    const floor = await selectedDesk.floor;

    if (!floor) {
      this.handleSelectEmployeeError(selectedEmployee.name);
      return;
    }

    // May be undefined when employee's desk is on a different floor than currently loaded
    const selectedMapFeature = mapFeatures.find((mapFeature) => mapFeature.externalId === selectedDesk.id);

    // Clear nextAssignment when using legacy path (no GraphQL data)
    if (onEmployeeSelect) {
      onEmployeeSelect(undefined);
    }

    // Transition to the correct floor; selectedMapFeature may be undefined for
    // cross-floor searches (desk highlight will not appear but floor redirect will)
    await this.handleTransition(selectedMapFeature?.id, floor.id, selectedEmployee.id);

    if (closeDropdown) {
      closeDropdown();
    }
    if (setSearchInput) {
      setSearchInput(selectedEmployee.name);
    }
  });

  async findEmployeeDesk(selectedEmployee: EmployeeDisplayItem, allDesks: DeskModel[]): Promise<DeskModel | undefined> {
    if (this.args.mapType === 'live') {
      // In live map: try reserved desk first, then fall back to assigned desk
      const reservedDeskState = this.liveMap.getUserReservation(selectedEmployee.id);
      if (reservedDeskState) {
        const reservedDesk = allDesks.find((desk) => desk.id === reservedDeskState.resourceId);
        if (reservedDesk) {
          return reservedDesk;
        }
      }

      // Fall back to assigned desk
      const assignedDesk = allDesks.find((desk) => desk.assignedTo === selectedEmployee.email);
      if (assignedDesk) {
        return assignedDesk;
      }

      // Last resort: the employee only has a reservation, and it may be on a different
      // floor than the one currently loaded — so it is absent from the floor-scoped
      // resourceStates above. Consult the location-wide reservations instead.
      return this.findReservedDeskAcrossFloors(selectedEmployee);
    } else {
      // In edit map: only look for assigned desk
      return allDesks.find((desk) => desk.assignedTo === selectedEmployee.email);
    }
  }

  async findReservedDeskAcrossFloors(selectedEmployee: EmployeeDisplayItem): Promise<DeskModel | undefined> {
    const reservation = this.findActiveReservationForEmployee(selectedEmployee);
    if (!reservation) {
      return undefined;
    }

    const deskId = reservation.belongsTo('desk').id();
    if (!deskId) {
      return undefined;
    }

    // The desk may sit on a floor that has not been loaded into the store: the live
    // route only loads the current floor's desks plus permanently-assigned ones, so a
    // reservation-only desk on another floor is absent from peekAll('desk'). Fetch it
    // on demand when it is not already present.
    const loadedDesk = this.store.peekRecord('desk', deskId);
    if (loadedDesk) {
      return loadedDesk;
    }

    // Include the floor so the subsequent floor transition can resolve it without a
    // second round trip. A fetch failure is allowed to propagate so the caller can
    // distinguish it from a genuinely missing reservation/desk.
    return this.store.findRecord('desk', deskId, { include: 'floor' });
  }

  findActiveReservationForEmployee(selectedEmployee: EmployeeDisplayItem): ReservationModel | undefined {
    const candidates = (this.args.reservations ?? []).filter(
      (reservation) => reservation.userEmail === selectedEmployee.email && !reservation.canceledAt,
    );

    const { selectedStartTime, selectedEndTime } = this.args;

    // Constrain to the window the live map is showing — intentionally STRICTER than the
    // controller's currentUserReservation, which only applies a window when both
    // selectedTime and selectedEndTime are set and otherwise matches any of the user's
    // reservations. Here we always window (never the browser clock, which would wrongly
    // reject an earlier part of the selected day or a future date) so a cross-floor search
    // lands on the reservation the user is actually viewing:
    // - partial-day: the explicit [start, end] window
    // - all-day for a date (end is null): the whole selected day
    // - no selection: the rest of the current day
    let windowStart: number;
    let windowEnd: number;
    if (selectedStartTime != null) {
      windowStart = selectedStartTime;
      windowEnd = selectedEndTime ?? selectedStartTime + SECONDS_IN_A_DAY;
    } else {
      windowStart = getUnixTime(new Date());
      windowEnd = windowStart + SECONDS_IN_A_DAY;
    }

    return candidates
      .filter((reservation) => reservation.startTime < windowEnd && reservation.endTime > windowStart)
      .sort((a, b) => a.startTime - b.startTime)[0];
  }
}
